import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { find, keys } from 'lodash'
import { nanoid } from 'nanoid'

import { firebase } from '../../core/services'
import { areSameValue } from '../../core/utils'

import {
  ANALYTICS_EVENT_CATEGORIES,
  ANALYTICS_EVENTS,
} from '../../common/constants'
import { aiHttpService, apolloService } from '../../common/services'

import { DISH_AI_SUGGESTIONS_ENDPOINT } from '../constants'
import { UPDATE_DISH_WITH_MANUAL_AND_AI_SUGGESTIONS_MUTATION } from '../graphql/mutations'
import { UPDATE_DISH_AI_SUGGESTIONS } from '../redux/action-types'
import {
  updateDishAiSuggestionsFail,
  updateDishAiSuggestionsStart,
} from '../redux/actions'
import {
  transformDishAiResponseAndManualChangesToUpdateArgs,
  transformDishToAiCurationRequest,
} from '../transformers'

const useDishSuggestCurationAndUpdate = dishId => {
  const dispatch = useDispatch()

  const dish = useSelector(store =>
    find(store.dishes.dishes?.data?.result, ({ id }) =>
      areSameValue(id, dishId),
    ),
  )

  const { data: menuCurationTask, locationNames } = useSelector(
    store => store.menuCurationTasks.menuCurationTask,
  )

  const suggestAndUpdate = useCallback(
    async (
      {
        shouldSuggestOnlyCourseTypeAndIngredients,
        shouldSuggestOnlyLabelsAndIngredients,
        shouldSuggestOnlyIngredients,
      } = {},
      manualChanges = {},
    ) => {
      try {
        dispatch(updateDishAiSuggestionsStart({ id: dishId }))

        const logData = {
          dishId,
          sessionId: nanoid(),
          changedFields: keys(manualChanges),
          options: {
            shouldSuggestOnlyCourseTypeAndIngredients,
            shouldSuggestOnlyLabelsAndIngredients,
            shouldSuggestOnlyIngredients,
          },
        }

        firebase.logEvent(
          ANALYTICS_EVENTS.MENU_CURATOR_UPDATE_DISH
            .MENU_CURATOR_START_AI_UPDATE_DISH,
          {
            eventCategory: ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_UPDATE_DISH,
            ...logData,
          },
        )

        const response = await aiHttpService.request(
          DISH_AI_SUGGESTIONS_ENDPOINT,
          transformDishToAiCurationRequest({
            dish,
            menuCurationTask,
            locationNames,
            options: {
              shouldSuggestOnlyCourseTypeAndIngredients,
              shouldSuggestOnlyLabelsAndIngredients,
              shouldSuggestOnlyIngredients,
            },
            manualChanges,
          }),
        )

        firebase.logEvent(
          ANALYTICS_EVENTS.MENU_CURATOR_UPDATE_DISH
            .MENU_CURATOR_END_AI_UPDATE_DISH,
          {
            eventCategory: ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_UPDATE_DISH,
            ...logData,
          },
        )

        firebase.logEvent(
          ANALYTICS_EVENTS.MENU_CURATOR_UPDATE_DISH
            .MENU_CURATOR_START_UPDATE_DISH,
          {
            eventCategory: ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_UPDATE_DISH,
            ...logData,
          },
        )

        await apolloService.mutate({
          mutation: UPDATE_DISH_WITH_MANUAL_AND_AI_SUGGESTIONS_MUTATION,
          variables: {
            id: dishId,
            data: transformDishAiResponseAndManualChangesToUpdateArgs(
              response,
              manualChanges,
            ),
          },
          dispatch,
          action: UPDATE_DISH_AI_SUGGESTIONS,
        })

        return firebase.logEvent(
          ANALYTICS_EVENTS.MENU_CURATOR_UPDATE_DISH
            .MENU_CURATOR_END_UPDATE_DISH,
          {
            eventCategory: ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_UPDATE_DISH,
            ...logData,
          },
        )
      } catch (error) {
        return dispatch(updateDishAiSuggestionsFail(error, { id: dishId }))
      }
    },
    [dish, dishId, dispatch, locationNames, menuCurationTask],
  )

  return [suggestAndUpdate, { isLoading: dish?.isLoading, error: dish?.error }]
}

export default useDishSuggestCurationAndUpdate
